/**
 * DOCUMENTATION:
 * Component: GCPCompute
 * Purpose: Deep dive into GCE (Compute Engine), MIGs, and Autoscaling logic.
 * * * Note for Junior Developers:
 * - GCE: Infrastructure as a Service (IaaS). You manage the OS and the patches.
 * - Instance Templates: The 'Blueprint' for your VMs. You define the OS, disk, and script once.
 * - MIG (Managed Instance Group): A collection of identical VMs. This is how we achieve
 * High Availability (HA) and Scalability.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from "@mui/material";

/* Icons */
import StorageIcon from '@mui/icons-material/Storage';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 65, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.2)",
    color: "white",
}));

export default function GCPCompute() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h2"} fontWeight={900} color="#4285F4">
                    COMPUTE ENGINE (GCE)
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>Infrastructure at Scale</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>

                {/* 1. Machine Families */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <StorageIcon sx={{ color: "#4285F4", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Choosing the Right Engine</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                            Google Cloud offers specialized "Machine Families." You don't just pick a CPU;
                            you pick a family based on your workload's personality.
                        </Typography>



                        <TableContainer component={Paper} sx={{ bgcolor: "rgba(0,0,0,0.2)", mt: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>Family</TableCell>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>Best For...</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>General Purpose (E2, N2)</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>Web servers, small DBs, dev environments.</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Compute Optimized (C2)</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>High-performance computing (HPC), gaming, encoding.</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Memory Optimized (M2)</TableCell>
                                        <TableCell sx={{ color: "white", opacity: 0.8 }}>Large In-memory DBs like SAP HANA.</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionCard>
                </Grid>

                {/* 2. Managed Instance Groups (MIGs) */}
                <Grid item xs={12} md={8}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <AutoFixHighIcon sx={{ color: "#34A853" }} />
                            <Typography variant="h5" fontWeight={800}>MIGs: The Scaling Secret</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ lineHeight: 1.8, opacity: 0.8, mb: 3 }}>
                            A **Managed Instance Group (MIG)** uses an <strong>Instance Template</strong> to create
                            identical VMs. For the PCA exam, remember the two "Auto" superpowers:
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: "rgba(52, 168, 83, 0.1)", border: "1px solid #34A853", borderRadius: "8px" }}>
                                    <Typography variant="subtitle2" color="#34A853">Auto-Healing</Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                        GCP monitors a 'Health Check'. If your app crashes, the MIG <strong>deletes and recreates</strong> the VM automatically.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: "rgba(66, 133, 244, 0.1)", border: "1px solid #4285F4", borderRadius: "8px" }}>
                                    <Typography variant="subtitle2" color="#4285F4">Autoscaling</Typography>
                                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                        Adds or removes VMs based on CPU load, HTTP traffic, or custom Cloud Monitoring metrics.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>


                    </SectionCard>
                </Grid>

                {/* 3. Cost & Performance */}
                <Grid item xs={12} md={4}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <TrendingUpIcon sx={{ color: "#FBBC04" }} />
                            <Typography variant="h5" fontWeight={800}>Optimization</Typography>
                        </Stack>

                        <Stack spacing={2}>
                            <Box sx={{ p: 2, bgcolor: "rgba(251, 188, 4, 0.1)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2" color="#FBBC04">Spot VMs</Typography>
                                <Typography variant="caption">Save up to 91% on costs. Google can "preempt" (shut down) these VMs with a 30-second notice.</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(255, 255, 255, 0.05)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2">Sole-Tenant Nodes</Typography>
                                <Typography variant="caption">Physical servers dedicated to just your projects. Used for strict compliance and licensing (BYOL).</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(255, 255, 255, 0.05)", borderRadius: "8px" }}>
                                <Typography variant="subtitle2">Shielded VMs</Typography>
                                <Typography variant="caption">Verifiable integrity via Secure Boot and vTPM to prevent rootkits.</Typography>
                            </Box>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* 4. Global Load Balancing */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <SpeedIcon sx={{ color: "#EA4335" }} />
                            <Typography variant="h5" fontWeight={800}>Global Load Balancing</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                            To make your MIGs accessible worldwide, we put them behind a **Global HTTP(S) Load Balancer**.
                            This uses <strong>Anycast IP</strong>—one single IP address for the entire world.
                        </Typography>



                        <Alert severity="success" sx={{ mt: 2, bgcolor: "rgba(52, 168, 83, 0.1)", color: "white", border: "1px solid #34A853" }}>
                            <strong>PCA ARCHITECT SCENARIO:</strong> "Your company expects a massive traffic spike in Asia but usually
                            operates in the US."
                            <br/><br/>
                            <strong>Solution:</strong> Use a <strong>Regional MIG</strong> in both regions and a
                            <strong> Global Load Balancer</strong>. The LB will automatically route users to the closest healthy instance.
                        </Alert>
                    </SectionCard>
                </Grid>

                {/* Navigation */}
                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
